define([
	"dojo/_base/declare",
	"dojo/_base/fx",
	"dojo/_base/lang",
	"dojo/aspect",
	"dojo/topic",
	"dojo/when",
	"csi",
	"csi/_base/underscore",
	"csi/action/Controller",
	"csi/action/FormData",
	"csi/def/Action",
	"csi/html",
	"csi/session",
	"csi/state",
	"csi/utility/routerUtility",
	"csi/xml"
], function(declare, baseFx, lang, aspect, topic, when, csi, _, Controller, FormData, Action, html, session, state, routerUtility, xml) {

	return declare(null, {
		// Boolean: indicates whether switchboard should use window location to track selection changes
		//bSelectIfOne: false,

		// Boolean: indicates whether switchboard should use window location to track selection changes
		bUseLocation: true,

		// DOMNode: attach points
		insertNode: null,

		/** @property {boolean} bMaintainSelectionOnRefresh - set to true if current selection should be maintained on refresh, or select first if not present */
		bMaintainSelectionOnRefresh: true,

		// Boolean: indicates widget is a switchboard
		bSwitchboard: true,

		// String: url of initially selected node which is reset after selected
		//urlPreSelect: null,

		// Deferred: deferred instance for loading content view
		//_dfdContent: null,

		// FormData: form data for content view
		//_formDataContent: null,

		// String: the key reserved in URL
		//_keyLocation: null,

		// Boolean: used internally to distinguish resize related to insertNode CV change
		_viewPrefChanged: false,

		getNextResultUrl: function(/*Boolean*/bPrevious) {
			// summary: determine if there is a next node in the store for current query
			var qr = this.getQueryResult(null, true);
			var node = qr && this.defView.m_formData.getSelectedNode();
			return node ? qr.getNextResultUrl(node.$URL, bPrevious) : null;
		},

		getSwitchboardSelectedNode: function() {
			// summary: return currently selected node in switchboard - use view cache if possible
			// as it will have the node with the latest updates
			var node = this.defView.m_formData.getSelectedNode();
			if (node) {
				return this.cacheView.getNode(node.$URL) || node;
			}

			return null;
		},

		onSwitchView: function(/*csi.view.Widget*/view) {
			// summary: extension point called when content view is created
		},

		_getFormDataWidget: function() {
			// summary: return widget holding form data to validate
			return this.viewLayout;
		},

		_onLocationChange: function(/*String*/value) {
			// summary: select new node based on change to document location
			var fd = this.defView.m_formData;
			var node = this.cacheView.getNode(value, true);
			var urlOld = session.getLocationActual(this._keyLocation);
			var dfd;

			this._switchLocation(node);

			if (node) {
				var ctx = this.getContext(value);
				fd.changeSelection(ctx, true);
				dfd = this._switchNodeOn(ctx, value, xml.isValidURL(urlOld));
			} else {
				this._switchReset();
			}

			this.postAction();
			return dfd;
		},

		_onSwitchNode: function(/*Context*/ctx, /*String*/url, /*Boolean*/bOn, /*Boolean*/bReplaced) {
			// summary: switch content view to that of selected node if switching on and if key is different than before
			var key = this._keyLocation;
			var value = bOn ? url : csi.EMPTY_URL;

			// set hash to track selection change - only do so when selecting a new node or unselecting a node
			// without replacing with a new selection
			if (key && (value != session.getLocationValue(key)) && (bOn || !bReplaced)) {
				this._switchLocation(ctx.getNode(value));
			}

			if (bOn) {
				// load content view based on selected node
				this._switchNodeOn(ctx, url, bReplaced);
			} else if (!bReplaced) {
				// reset content view when selection undone
				this._switchNodeOff();
			}

			// enable toolbar now that node is selected for switchboard
			if (this.toolbarNode) {
				this.toolbarNode.enablePlugins();
			}
		},

		_switchLocation: function(/*Node?*/node) {
			// summary: update location with new url
			if (this._keyLocation) {
				session.setLocationActual(this._keyLocation, node ? node.$URL : csi.EMPTY_URL, null, lang.hitch(this, this._onLocationChange));
			}
		},

		_switchLocationKey: function() {
			// summary: track selected node for page in session
			if (state.get("print")) {
				return;
			}

			if (this._keyLocation) {
				session.releaseLocationKey(this._keyLocation);
			}

			if (this.bUseLocation && !this.bPopup && this.defView.idContentView) {
				this._keyLocation = session.reserveLocationKey(routerUtility.LOCATION_SELECTED_URL, lang.hitch(this, this._onLocationChange));
			}
		},

		_switchPostCreate: function() {
			// summary: setup form data to allow node selection
			if (state.get("print")) {
				return;
			}

			var controller = new Controller({
				m_action: new Action(),
				m_element: this.domNode,
				widget: this
			});

			// if switchboard view part of form, then forward form data to content view
			var fd = this._formDataContent = this.defView.m_formData;

			var fd2 = lang.mixin(new FormData(controller), {
				m_bEditable: !fd || fd.m_bEditable,
				m_bSelectOne: true,
				m_bRequireOne: true
			});

			// if no content view and view is read-only or slim, then no reason to allow nodes to be selected; if slim, then no toolbar, which
			// means no actions that will be applied to selections
			if ((this.defView.isReadOnly() || this.defView.isSlim()) && !this.defView.idContentView) {
				fd2.m_bAllowSelections = false;
			} else {
				fd2.m_bAllowAddNewFocus = true;
				fd2.addSelectListener((ctx, url, bOn, bReplaced) => this._onSwitchNode(ctx, url, bOn, bReplaced));
			}

			this.defView.setFormData(fd2);

			// determine if hash specifies the first location
			if (this._keyLocation) {
				this.urlPreSelect = session.getLocationValue(this._keyLocation);
			}

			// if in popup dialog, actions should go below content view
			if (this.bPopup && this.actionsNode) {
				html.destroyElement(this.actionsNode);
				this.actionsNode = null;
			}
		},

		_switchPostView: function(/*Context*/ctx) {
			// summary: select default node after building/refreshing view
			if (state.get("print")) {
				html.setVisible(this.insertNode, false);
				return null;
			}

			this._switchSetDefaultSelection();

			var fd = this.defView.m_formData;
			var node = this.cacheView.getNode(this.urlPreSelect);
			var ctxSelection = node ? this.getContext(node.$URL) : null;

			this.urlPreSelect = null;
			this._dfdContent = null;

			// keep location in sync with selected node - do so before changing selection or else will
			// trigger change to location history
			this._switchLocation(node);

			if (node) {
				fd.changeSelection(ctxSelection, true);

				this.when(this._dfdContent, lang.hitch(this, function() {
					_.delay(function(w) {
						w.scrollIntoView(w.getRefNodeByUrl(node.$URL));
					}, 0, this);
				}));
			} else {
				this._switchReset();
			}

			// return Deferred created in _switchSelect
			return this._dfdContent;
		},

		/**
		 * When view is refreshed, determine if selection should be reset or not
		 */
		_switchRefresh: function() {
			if (!this.bMaintainSelectionOnRefresh) {
				this._switchReset();
			}
		},

		_switchReset: function() {
			// summary: reset selections and hide content view
			this.defView.m_formData.resetSelections(null, true);
			this._switchNodeOff();

			// update toolbar now that nothing is selected
			if (this.toolbarNode) {
				this.toolbarNode.enablePlugins();
			}
		},

		_switchSelect: function(/*Node*/node) {
			// summary: refresh the content view based on the URL of selected node
			if (!node) {
				return null;
			}

			if (this.viewLayout) {
				// refresh content view if already created
				this.viewLayout.setViewNode(node, true);
				this._dfdContent = this.viewLayout.refreshWidget();
			} else if (this.defView.idContentView) {
				// create content view
				this._dfdContent = this._insertAndCreateView(this.defView.idContentView, node, this.insertNode, {
					bInSwitchboard: true,
					formData: this._formDataContent
				});

				this._dfdContent.then((view) => {
					this.viewLayout = view;
					this.onSwitchView(view);

					// If used in popup, and user change insertNode's CV, then whole dialog need to be resized
					if (this.bPopup) {
						this.own(
							aspect.after(view, "onViewPreferences", () => {
								this._viewPrefChanged = true;
							}),
							aspect.after(view, "_onResize", () => {
								if (this._viewPrefChanged) {
									this._viewPrefChanged = false;
									topic.publish("/csi/dialog/resize/required", false);
								}
							})
						);
					}
				});
			}

			return this._dfdContent;
		},

		_switchNodeOn: function(/*Context*/ctx, /*String*/url, /*Boolean*/bReplaced) {
			// summary: switch content view to that of selected node
			const dfd = this._switchSelect(this.cacheView.getNode(url));
			const insertNode = this.insertNode;

			when(dfd, function(viewLayout) {
				html.setVisibility(insertNode, true);
				baseFx.fadeIn({node: insertNode}).play();
			});

			return dfd;
		},

		_switchNodeOff: function(/*Boolean*/bReplaced) {
			// summary: destroy content view when node de-selected
			const insertNode = this.insertNode;

			baseFx.fadeOut({
				node: insertNode,
				onEnd: function() {
					html.setVisibility(insertNode, false);
				}
			}).play();
		},

		/**
		 * Determine first node to select in view, if any
		 */
		_switchSetDefaultSelection: function() {
			var url = this.urlPreSelect;
			var bValid = xml.isValidURL(url);

			if (!bValid && this.bMaintainSelectionOnRefresh) {
				var selected = this.getSwitchboardSelectedNode();
				if (selected) {
					url = selected.$URL;
					bValid = true;
				}
			}

			if (!bValid || !this.getQueryResult(url)) {
				url = null;

				if (this.defView.bSelectFirst || this.bSelectIfOne || (bValid && this.bMaintainSelectionOnRefresh)) {
					var candidates = [];
					var n = 0;

					_.some(this.getRefNodes(), function(refNode) {
						const qr = this.getQueryResultByDomNode(refNode);

						if (qr.level.bMain) {
							candidates.push(qr.url);
							++n;
						}

						return n == 2;
					}, this);

					if (n && (!this.bSelectIfOne || (n === 1))) {
						url = candidates[0];
					}
				}
			}

			this.urlPreSelect = url;

			// reset because this setting is transient
			this.bSelectIfOne = false;
		},

		// @PostAction functions
		postActionRefreshSwitchboard: function(/*Controller*/ac) {
			// summary: change made on node should refresh switchboard content if currently selected
			var selected = this.getSwitchboardSelectedNode();
			if (selected && (selected.$URL == ac.m_urlNode) && this.viewLayout) {
				this.viewLayout.setViewNode(selected, true);
				this.viewLayout.refreshWidget();
			}
		}
	});
});